import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, AreaChart, Area, CartesianGrid } from "recharts";
import { Trophy } from "lucide-react";

export default function AdminReports() {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/reports"); setData(data); }
      catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    })();
  }, []);

  if (!data) return <p className="text-[#7248ae]">Cargando reportes…</p>;

  const stats = [
    { label: "Hoy", value: formatCurrency(data.today_sales) },
    { label: "Semana", value: formatCurrency(data.week_sales) },
    { label: "Mes", value: formatCurrency(data.month_sales) },
    { label: "Total histórico", value: formatCurrency(data.total_sales) },
  ];

  const dailyTrim = data.daily_series.slice(-14); // last 14 days
  const topDishes = data.dish_ranking.slice(0, 10);
  const bottomDishes = data.dish_ranking.slice(-5).reverse();

  return (
    <div className="animate-fade-up" data-testid="admin-reports-page">
      <p className="text-xs uppercase tracking-[0.3em] text-[#7248ae] mb-2">Métricas</p>
      <h1 className="font-display text-4xl sm:text-5xl font-black text-[#1a0f2e] mb-8 tracking-tight">Reportes</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((s, i) => (
          <Card key={i} className={`p-5 border border-[#b182ec]/30 rounded-2xl card-glow animate-fade-up stagger-${i+1}`} data-testid={`report-stat-${i}`}>
            <p className="text-xs uppercase tracking-wider text-[#7248ae] mb-2">{s.label}</p>
            <p className="font-display text-2xl sm:text-3xl font-black text-[#1a0f2e]">{s.value}</p>
          </Card>
        ))}
      </div>

      <Card className="p-6 border border-[#b182ec]/30 rounded-2xl mb-8">
        <h2 className="font-display text-xl font-bold text-[#1a0f2e] mb-1">Ventas — últimos 14 días</h2>
        <p className="text-sm text-[#7248ae] mb-4">Tendencia diaria de ingresos pagados.</p>
        <div className="w-full h-64" data-testid="sales-chart">
          <ResponsiveContainer>
            <AreaChart data={dailyTrim}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3e166c" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#3e166c" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#b182ec40" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#7248ae", fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fill: "#7248ae", fontSize: 11 }} />
              <Tooltip
                contentStyle={{ background: "#ffffff", border: "1px solid #b182ec40", borderRadius: 12, color: "#1a0f2e" }}
                formatter={(v) => formatCurrency(v)}
              />
              <Area type="monotone" dataKey="total" stroke="#3e166c" strokeWidth={2.5} fill="url(#g1)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="p-6 border border-[#b182ec]/30 rounded-2xl" data-testid="top-dishes-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-xl bg-[#3e166c] flex items-center justify-center">
              <Trophy className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="font-display text-xl font-bold text-[#1a0f2e]">Top vendidos</h2>
              <p className="text-sm text-[#7248ae]">Los favoritos de tus clientes.</p>
            </div>
          </div>
          <div className="w-full h-64 mb-4">
            <ResponsiveContainer>
              <BarChart data={topDishes} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid stroke="#b182ec40" horizontal={false} />
                <XAxis type="number" tick={{ fill: "#7248ae", fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fill: "#7248ae", fontSize: 11 }} width={120} />
                <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #b182ec40", borderRadius: 12 }} />
                <Bar dataKey="quantity" fill="#3e166c" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border border-[#b182ec]/30 rounded-2xl" data-testid="bottom-dishes-card">
          <h2 className="font-display text-xl font-bold text-[#1a0f2e] mb-1">Menos vendidos</h2>
          <p className="text-sm text-[#7248ae] mb-4">Los que necesitan promoción.</p>
          <ul className="space-y-3">
            {bottomDishes.map(d => (
              <li key={d.dish_id} className="flex items-center justify-between p-3 rounded-lg bg-[#fcfbfd] border border-[#b182ec]/20">
                <span className="text-sm text-[#1a0f2e]">{d.name}</span>
                <span className="text-xs uppercase tracking-wider text-[#7248ae] font-semibold">{d.quantity} ventas</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <Card className="p-6 border border-[#b182ec]/30 rounded-2xl" data-testid="dish-ranking-card">
        <h2 className="font-display text-xl font-bold text-[#1a0f2e] mb-4">Ranking completo</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-[#7248ae] border-b border-[#b182ec]/30">
                <th className="py-2 pr-4">#</th>
                <th className="py-2 pr-4">Platillo</th>
                <th className="py-2 pr-4">Cantidad</th>
                <th className="py-2 pr-4">Ingresos</th>
              </tr>
            </thead>
            <tbody>
              {data.dish_ranking.map((d, i) => (
                <tr key={d.dish_id} className="border-b border-[#b182ec]/15 text-sm text-[#1a0f2e]" data-testid={`rank-row-${i}`}>
                  <td className="py-2 pr-4 font-mono text-[#7248ae]">{i+1}</td>
                  <td className="py-2 pr-4">{d.name}</td>
                  <td className="py-2 pr-4">{d.quantity}</td>
                  <td className="py-2 pr-4">{formatCurrency(d.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
